<template>
	<div class="settings-container">
		<div class="row">
			<div v-show="currencyStore.currCurrency.code === 'EUR'" class="split-col-user">
				<UserReferralContainer />
			</div>
			<div class="split-col-user">
				<AppCard :title="getString('user_info')" class="card-management card-user-info" :button-text="getString('save')" @button-click="updateAccount" :loading="loadingInfo">
					<template #body>
						<AppList :items="userInfos" />
					</template>
				</AppCard>
				<AppCard :title="getString('2_factor_authentication')" class="card-management">
					<template v-slot:body>
						<div class="new-auth-container" v-if="userStore.user && !userStore.user.tfa_secret">
							<div class="generate-code-row collapsing-row row space-between" v-if="!code">
								<p>{{ getString('generate_code') }}</p>
								<AppButton class="ml-auto" :text="getString('generate')" @click="getNewSecret" />
							</div>
							<div class="code-row-container" v-if="code">
								<div class="code-row collapsing-row row space-between">
									<div class="instructions-container">
										<p>{{ getString('scan_tfa_code') }}</p>
										<p><a :href="otpAuthLink">{{ getString('or_click_here') }}</a></p>
										<p class="backup mt-10">{{ getString('save_this_code_safely_for_backup', [this.otpAuthLink]) }}</p>
									</div>
									<AppQrImage class="ml-auto" :value="otpAuthLink" />
								</div>
								<div class="line"></div>
								<div class="verify-row collapsing-row row space-between mt-15">
									<p>{{ getString('verify_code_to_confirm') }}</p>
									<div class="inputs-container ml-auto">
										<AppTextInput :placeholder="getString('2fa_code')" ref="verify-code-input" />
										<AppButton :text="getString('verify')" @click="verifyCode" :loading="loading" />
									</div>
								</div>
								<div class="disclaimer-row">
									<p>{{ getString('disclaimer_2fa') }}</p>
								</div>
							</div>
						</div>
						<div class="auth-setup" v-else>
							<p>{{ getString('2fa_setup_successfully') }}</p>
						</div>
					</template>
				</AppCard>
			</div>
		</div>
		<AppNotification :title="getString('success')" :body-text="getString('changes_saved')" :type="1" ref="generic-success" />
		<AppNotification :title="getString('success')" :body-text="getString('tfa_success')" :type="1" ref="tfa-success" />
		<AppNotification :title="getString('error')" :body-text="getString('try_later_error')" :type="-2" ref="tfa-fail" />
		<AppNotification :title="getString('error')" :body-text="getString('wrong_user_info')" :type="-2" ref="user-fail" />
		<AppNotification :title="getString('error')" :body-text="getString('verification_failed')" :type="-2" ref="verification-fail" />
	</div>
</template>

<script>
import UserReferralContainer from "@/user/components/settings/UserReferralContainer.vue";
import AppCard from "@/shared/components/AppCard.vue";
import {useLanguageStore, useCurrencyStore} from "@/shared/stores";
import {useUserStore} from "@/user/stores";
import AppButton from "@/shared/components/AppButton.vue";
import AppQrImage from "@/shared/components/AppQrImage.vue";
import AppTextInput from "@/shared/components/AppTextInput.vue";
import AppNotification from "@/shared/components/AppNotification.vue";
import API from "@/shared/helpers/API";
import AppList from "@/shared/components/AppList.vue";
import {validatePhone} from "@/shared/helpers/strings";

export default {
	setup() {
		const languageStore = useLanguageStore();
		const userStore = useUserStore();
		const currencyStore = useCurrencyStore();
		return {getString: languageStore.getString, currencyStore, userStore}
	},
	name: "SettingsContainer",
	components: {AppList, AppNotification, UserReferralContainer, AppTextInput, AppQrImage, AppCard, AppButton},
	data() {
		return {
			loading: false,
			loadingInfo: false,
			code: null,
			tempPicture: null,
		}
	},
	computed: {
		otpAuthLink() {
			if(this.code) {
				return `otpauth://totp/${ENVIRONMENT}:${this.userStore.user.email}?secret=${this.code}&issuer=qromo&algorithm=SHA1&digits=6&period=30`;
			}
		},
		userInfos() {
			if(!this.userStore.user) return [];
			return [
				{title: this.getString('email'), rightText: this.userStore.user.email, rightPrimaryText: true},
				{title: this.getString('name'), textInput: true, placeholder: this.getString('name'), value: this.userStore.user.name ? this.userStore.user.name  : '', callbackText: (value) => this.userStore.user.name = value},
				{title: this.getString('phone'), textInput: true, placeholder: this.getString('phone'), value: this.userStore.user.phone ? this.userStore.user.phone  : '', callbackText: (value) => this.userStore.user.phone = value},
				{
					title: this.getString('profile_picture'),
					imageSlot: true,
					image: this.userStore.user.picture ? this.getImageUrl(this.userStore.user.picture) : null,
					maxWidth: 300,
					maxHeight: 300,
					maxBytes: 1024 * 100,
					callbackImage: (fileName) => this.onPictureUploaded(fileName),
					callbackRemove: () => this.removePicture()
				},
			]
		}
	},
	methods: {
		getImageUrl(filename) {
			if (!filename) return '';
			if (filename.startsWith('http')) return filename;
			return `https://qromomilan.s3.eu-south-1.amazonaws.com/businesses/${filename}`;
		},
		onPictureUploaded(fileName) {
			this.tempPicture = fileName;
			this.userStore.user.picture = fileName;
		},
		removePicture() {
			this.userStore.user.picture = null;
			this.tempPicture = null;
		},
		updateAccount() {
			this.loadingInfo = true;
			if(!this.userStore.user.name || !this.userStore.user.phone || !validatePhone(this.userStore.user.phone)) {
				this.$refs["user-fail"].openNotification();
				this.loadingInfo = false;
				return;
			}
			API.init().updateAccount(this.userStore.user.name, this.userStore.user.phone, this.userStore.user.picture, (response) => {
				this.loadingInfo = false;
				if(response.result && response.result === "OK") {
					this.$refs["generic-success"].openNotification();
				} else {
					this.$refs["tfa-fail"].openNotification();
				}
			})
		},
		getNewSecret() {
			this.loading = true;
			API.init().getTwoFactorAuthSecret((response) => {
				this.loading = false;
				if(response.result && response.result === "OK") {
					this.code = response.secret;
				} else {
					this.$refs["tfa-fail"].openNotification();
				}
			})
		},
		verifyCode() {
			if(this.$refs["verify-code-input"]) {
				const code = this.$refs["verify-code-input"].currentValue
				if(code.length === 6) {
					this.loading = true;
					API.init().addTwoFactorAuth(this.code, code, (response) => {
						this.loading = false;
						if(response.result && response.result === "OK") {
							this.userStore.user.tfa_secret = "***";
							this.code = null;
							this.$refs["tfa-success"].openNotification();
						} else {
							if(response.details && response.details === "verification") {
								this.$refs["verification-fail"].openNotification();
							} else {
								this.$refs["tfa-fail"].openNotification();
							}
						}
					})
				} else {
					this.$refs["verification-fail"].openNotification();
				}
			}
		}
	}
}
</script>

<style scoped lang="scss">
.settings-container {
	max-width: 1400px;
	margin: 0 auto;
	display: flex;
	justify-content: center;
	align-items: flex-start;
	gap: 30px;
	flex-wrap: wrap;
	padding: var(--main-padding);

	.card-management {
		margin-bottom: 30px;
	}

	.card-user-info {

		&:deep(.card-body) {
			padding: 0 !important;
		}
	}

	.split-col-user {
		display: flex;
		flex-direction: column;
		width: 50%;
		padding: 0;

		&:first-child {
			padding-right: 20px;
		}
		&:last-child {
			padding-left: 20px;
		}

		@media (max-width: 1080px) {
			&:first-child {
				padding-right: 0;
				margin-bottom: 60px;
			}
			&:last-child {
				padding-left: 0;
			}
			width: 100%;
		}
	}

	.collapsing-row {
		gap: 8px;
	}

	.ml-auto {
		margin-left: auto;
	}

	.backup {
		font-size: 11px;
		line-height: 13px;
		color: var(--color-txt-1);
		white-space: pre-wrap;
		white-space: -moz-pre-wrap;
		white-space: -pre-wrap;
		white-space: -o-pre-wrap;
		word-wrap: break-word;
	}

	.inputs-container {
		display: flex;
		gap: 8px;
		flex-direction: column;
		align-items: flex-end;

		.input-wrapper {
			width: auto;
		}
	}

	.instructions-container {
		max-width: calc(100% - 180px);
	}

	.disclaimer-row {
		p {
			margin-top: 15px;
			font-size: 12px;
			color: $danger;
		}
	}

	.line {
		width: 100%;
		height: 1px;
		background-color: #ccc;
		margin: 15px 0;
	}

}
</style>